import { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { Upload, X, ArrowLeft } from "lucide-react";
import { toast } from "sonner";
import api, { imgUrl, formatApiError } from "../../lib/api";
import { Switch } from "../../components/ui/switch";

const empty = {
  code: "", category: "", name: "", name_en: "", description: "", description_en: "",
  price: 0, stock: 0, material: "", weight: "", size: "", length: "", clasp: "", color: "",
  images: [], featured: false, is_active: true, variants: [],
};

// Geyimdə ən çox işlənən ölçülər — bir toxunuşla əlavə olunsun deyə.
const COMMON_SIZES = ["XS", "S", "M", "L", "XL", "XXL"];

const AdminProductForm = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const editing = id && id !== "new";
  const [form, setForm] = useState(empty);
  const [groups, setGroups] = useState([]);
  const [uploading, setUploading] = useState(false);
  const [saving, setSaving] = useState(false);

  // Admin gizlədilmiş kateqoriyaları da görməlidir (mövcud məhsulları redaktə etmək üçün).
  useEffect(() => {
    api.get("/admin/categories").then(({ data }) => {
      const gs = data.groups || [];
      setGroups(gs);
      // Yeni məhsulda ilk mövcud kateqoriya seçilir.
      const first = gs.flatMap((g) => g.children || [])[0];
      if (first) setForm((f) => (f.category ? f : { ...f, category: first.key }));
    });
  }, []);

  useEffect(() => {
    if (editing) {
      api.get(`/products/${id}`).then(({ data }) => setForm({ ...empty, ...data }));
    }
  }, [id, editing]);

  // Geyimdə bir məhsulun bir neçə ölçüsü olur (S, M, L); zinətdə tək ölçü.
  const groupOfSelected = (groups.find((g) =>
    (g.children || []).some((c) => c.key === form.category)) || {}).key;
  const isClothing = groupOfSelected === "clothing";
  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });
  const field = "w-full border border-brand-line bg-white px-4 py-2.5 text-sm outline-none focus:border-brand-ink transition-colors rounded-md";
  const labelCls = "overline block mb-2";

  const onUpload = async (e) => {
    const files = Array.from(e.target.files || []);
    if (!files.length) return;
    setUploading(true);
    try {
      for (const f of files) {
        const fd = new FormData();
        fd.append("file", f);
        const { data } = await api.post("/admin/upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
        setForm((prev) => ({ ...prev, images: [...prev.images, data.url] }));
      }
      toast.success("Şəkil yükləndi");
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    } finally {
      setUploading(false);
    }
  };

  // --- Ölçülər ---
  const variants = form.variants || [];
  const hasVariants = variants.length > 0;
  const variantTotal = variants.reduce((s, v) => s + (Number(v.stock) || 0), 0);

  const setVariants = (next) => setForm((f) => ({ ...f, variants: next }));

  const addSize = (size) => {
    const s = String(size || "").trim().toUpperCase();
    if (!s) return;
    if (variants.some((v) => v.size === s)) {
      toast.error(`${s} ölçüsü artıq var`);
      return;
    }
    setVariants([...variants, { size: s, stock: 0 }]);
  };

  const setVariantStock = (size, value) =>
    setVariants(variants.map((v) =>
      v.size === size ? { ...v, stock: Math.max(0, Number(value) || 0) } : v));

  const removeSize = (size) => setVariants(variants.filter((v) => v.size !== size));

  const removeImage = (url) => setForm((prev) => ({ ...prev, images: prev.images.filter((i) => i !== url) }));

  const addImageUrl = () => {
    const url = window.prompt("Şəkil linki (URL):");
    if (url) setForm((prev) => ({ ...prev, images: [...prev.images, url] }));
  };

  const save = async (e) => {
    e.preventDefault();
    if (!form.code || !form.name) {
      toast.error("Kod və ad mütləqdir");
      return;
    }
    setSaving(true);
    try {
      const payload = {
        ...form,
        price: Number(form.price),
        // Ölçülər varsa ümumi stok onların cəmidir — iki yerdə ayrı rəqəm
        // saxlamaq həmişə uyğunsuzluğa gətirir.
        stock: hasVariants ? variantTotal : Number(form.stock),
        variants: variants.map((v) => ({ size: v.size, stock: Number(v.stock) || 0 })),
        weight: form.weight === "" || form.weight === null ? null : Number(form.weight),
      };
      if (editing) await api.put(`/admin/products/${id}`, payload);
      else await api.post("/admin/products", payload);
      toast.success("Yadda saxlanıldı");
      navigate("/admin/products");
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="max-w-3xl" data-testid="admin-product-form">
      <button onClick={() => navigate("/admin/products")} className="flex items-center gap-2 overline mb-6 hover:text-brand-ink"><ArrowLeft size={16} /> Geri</button>
      <h1 className="font-serif font-light text-3xl sm:text-4xl tracking-tight mb-6 sm:mb-8">{editing ? "Məhsulu redaktə et" : "Yeni məhsul"}</h1>

      <form onSubmit={save} className="space-y-6 bg-white border border-brand-line rounded-md p-4 sm:p-8">
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          <div>
            <label className={labelCls}>Məhsul kodu *</label>
            <input className={field} value={form.code} onChange={set("code")} data-testid="form-code" />
          </div>
          <div>
            <label className={labelCls}>Bölmə *</label>
            <select className={field} value={form.category} onChange={set("category")} data-testid="form-category">
              {groups.map((g) => (
                <optgroup key={g.key} label={g.is_active === false ? `${g.name_az} (gizli)` : g.name_az}>
                  {(g.children || []).map((c) => (
                    <option key={c.key} value={c.key}>
                      {c.is_active === false ? `${c.name_az} (gizli)` : c.name_az}
                    </option>
                  ))}
                </optgroup>
              ))}
            </select>
          </div>
          <div>
            <label className={labelCls}>Ad (AZ) *</label>
            <input className={field} value={form.name} onChange={set("name")} data-testid="form-name" />
          </div>
          <div>
            <label className={labelCls}>Ad (EN)</label>
            <input className={field} value={form.name_en} onChange={set("name_en")} data-testid="form-name-en" />
          </div>
          <div>
            <label className={labelCls}>Qiymət (₼) *</label>
            <input type="number" step="0.01" className={field} value={form.price} onChange={set("price")} data-testid="form-price" />
          </div>
          <div>
            <label className={labelCls}>Stok sayı *</label>
            <input
              type="number"
              className={`${field} ${hasVariants ? "bg-brand-bg2 text-brand-muted" : ""}`}
              value={hasVariants ? variantTotal : form.stock}
              onChange={set("stock")}
              disabled={hasVariants}
              data-testid="form-stock"
            />
            {hasVariants && (
              <p className="text-xs text-brand-muted mt-1">
                Ölçülərin cəmi — aşağıdan idarə olunur
              </p>
            )}
          </div>
        </div>

        {/* Ölçülər. Eyni məhsulun XS/S/M/L/XL ölçüləri ayrı-ayrı sayılır. */}
        <div className="border border-brand-line rounded-md p-4 bg-white" data-testid="variants-box">
          <div className="flex flex-wrap items-center justify-between gap-2 mb-3">
            <label className="overline">Ölçülər və sayları</label>
            {hasVariants && (
              <span className="text-xs text-brand-muted">Cəmi: {variantTotal} ədəd</span>
            )}
          </div>

          {hasVariants ? (
            <div className="space-y-2 mb-4">
              {variants.map((v) => (
                <div key={v.size} className="flex items-center gap-3" data-testid={`variant-${v.size}`}>
                  <span className="w-14 shrink-0 text-sm font-medium">{v.size}</span>
                  <input
                    type="number"
                    min="0"
                    value={v.stock}
                    onChange={(e) => setVariantStock(v.size, e.target.value)}
                    data-testid={`variant-stock-${v.size}`}
                    className="w-28 border border-brand-line rounded-md px-3 py-2 text-sm outline-none focus:border-brand-ink"
                  />
                  <span className="text-xs text-brand-muted">ədəd</span>
                  {Number(v.stock) === 0 && (
                    <span className="text-xs text-destructive">satılıb</span>
                  )}
                  <button
                    type="button"
                    onClick={() => removeSize(v.size)}
                    data-testid={`variant-remove-${v.size}`}
                    className="ml-auto p-2 text-brand-muted hover:text-destructive"
                    title="Bu ölçünü sil"
                  >
                    <X size={16} strokeWidth={1.5} />
                  </button>
                </div>
              ))}
            </div>
          ) : (
            <p className="text-xs text-brand-muted mb-4">
              Bu məhsulun ölçüsü yoxdur — yuxarıdakı ümumi stok işlənir.
              Ölçü əlavə etsəniz stok hər ölçü üzrə ayrıca sayılacaq.
            </p>
          )}

          <div className="flex flex-wrap gap-2">
            {COMMON_SIZES.filter((s) => !variants.some((v) => v.size === s)).map((s) => (
              <button
                key={s}
                type="button"
                onClick={() => addSize(s)}
                data-testid={`add-size-${s}`}
                className="border border-brand-line rounded-md px-3 py-2 text-sm hover:border-brand-ink"
              >
                + {s}
              </button>
            ))}
            <button
              type="button"
              onClick={() => addSize(window.prompt("Ölçü adı (məs. 38, One size):"))}
              data-testid="add-size-custom"
              className="border border-dashed border-brand-line rounded-md px-3 py-2 text-sm hover:border-brand-ink"
            >
              + Başqa ölçü
            </button>
          </div>
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          <div>
            <label className={labelCls}>Material</label>
            <input className={field} value={form.material} onChange={set("material")} data-testid="form-material" />
          </div>
          <div>
            <label className={labelCls}>{isClothing ? "Ölçü(lər) — vergüllə (S, M, L)" : "Ölçü"}</label>
            <input className={field} value={form.size} onChange={set("size")} data-testid="form-size" />
          </div>
          {isClothing ? (
            <div className="sm:col-span-2">
              <label className={labelCls}>Rəng(lər) — vergüllə (Ağ, Qara)</label>
              <input className={field} value={form.color} onChange={set("color")} data-testid="form-color" />
            </div>
          ) : (
            <>
              <div>
                <label className={labelCls}>Çəki (qram)</label>
                <input
                  type="number"
                  step="0.01"
                  min="0"
                  placeholder="məs. 4.20"
                  className={field}
                  value={form.weight ?? ""}
                  onChange={set("weight")}
                  data-testid="form-weight"
                />
              </div>
              <div>
                <label className={labelCls}>Kilid</label>
                <input
                  placeholder="məs. Karabin"
                  className={field}
                  value={form.clasp}
                  onChange={set("clasp")}
                  data-testid="form-clasp"
                />
              </div>
              <div>
                <label className={labelCls}>Uzunluq</label>
                <input className={field} value={form.length} onChange={set("length")} data-testid="form-length" />
              </div>
              <div>
                <label className={labelCls}>Rəng</label>
                <input className={field} value={form.color} onChange={set("color")} data-testid="form-color" />
              </div>
            </>
          )}
        </div>

        <div className="grid grid-cols-1 gap-4">
          <div>
            <label className={labelCls}>Təsvir (AZ)</label>
            <textarea rows={3} className={field} value={form.description} onChange={set("description")} data-testid="form-description" />
          </div>
          <div>
            <label className={labelCls}>Təsvir (EN)</label>
            <textarea rows={3} className={field} value={form.description_en} onChange={set("description_en")} data-testid="form-description-en" />
          </div>
        </div>

        {/* Images */}
        <div>
          <label className={labelCls}>Şəkillər</label>
          <div className="flex flex-wrap gap-3 mb-3">
            {form.images.map((url) => (
              <div key={url} className="relative w-20 h-24 bg-brand-bg2 overflow-hidden rounded group">
                <img src={imgUrl(url)} alt="" className="w-full h-full object-cover" onError={(e) => { e.target.src = imgUrl(null); }} />
                <button type="button" onClick={() => removeImage(url)} className="absolute top-1 right-1 bg-brand-ink text-white p-0.5 rounded opacity-0 group-hover:opacity-100"><X size={12} /></button>
              </div>
            ))}
          </div>
          <div className="flex gap-3">
            <label className="flex items-center gap-2 border border-brand-line px-4 py-2 text-sm rounded-md cursor-pointer hover:border-brand-ink" data-testid="upload-image-label">
              <Upload size={16} strokeWidth={1.5} /> {uploading ? "Yüklənir..." : "Şəkil yüklə"}
              <input type="file" accept="image/*" multiple className="hidden" onChange={onUpload} data-testid="form-image-input" />
            </label>
            <button type="button" onClick={addImageUrl} className="border border-brand-line px-4 py-2 text-sm rounded-md hover:border-brand-ink">Link əlavə et</button>
          </div>
        </div>

        <div className="flex items-center gap-8">
          <div className="flex items-center gap-3">
            <Switch checked={form.featured} onCheckedChange={(v) => setForm({ ...form, featured: v })} data-testid="form-featured" />
            <span className="text-sm">Seçilmiş</span>
          </div>
          <div className="flex items-center gap-3">
            <Switch checked={form.is_active} onCheckedChange={(v) => setForm({ ...form, is_active: v })} data-testid="form-active" />
            <span className="text-sm">Aktiv</span>
          </div>
        </div>

        <button type="submit" disabled={saving} data-testid="save-product-btn" className="bg-brand-ink text-white px-8 py-3 text-sm tracking-[0.15em] uppercase rounded-md hover:bg-brand-oxid transition-colors disabled:opacity-50">
          {saving ? "..." : "Yadda saxla"}
        </button>
      </form>
    </div>
  );
};

export default AdminProductForm;
